import React, { useEffect, useMemo, useState } from 'react';
import { BarList, Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput, fmtDate, useAdminGet } from '../kit';
import { Pager, Pill, type Tone, num, pageInfo, rowsOf, str, useDebounced } from '../ipintel/shared';
import {
  EMPTY_TAG_FILTER, LifecyclePill, TagFilter, type TagFilterValue, TagPills, tagFilterActive, tagFilterParams,
} from '../tags/shared';
import {
  DailyBars, ExcelButton, OpenLink, type Range, SHARE_LABELS, breakdown, fmtInt, nameOf, rangeParams, useEscape,
} from './shared';

/**
 * Tech URL pages → Shares: every tracked share link (Backend/share_links.py,
 * admin section "shares" — Backend/admin_shares.py).
 *
 * A share link is one code per sharer (a signed-in user, else an anonymous
 * visitor) per target per surface: "platform" = the tech cards inside the app,
 * "techpage" / "unipage" = the public pages' share buttons. Each copy, channel
 * click or native share is an event on the code, and so is each open of the
 * link (IP, geo, user agent, bot or not, the viewer when signed in, and — via
 * site_visits — whether that visitor signed in or up later).
 *  - GET shares/overview {from, to, surface, exclude_internal}
 *  - GET shares/links {q, surface, channel, target_kind, lifecycle/tags…, from, to, sort, dir, page, per_page}
 *  - GET shares/link {code} → the drawer's event timeline
 *  - GET /api/v1/admin/shares/export.xlsx (same filters as the table)
 */

const SECTION = 'shares';
const EXPORT_PATH = '/api/v1/admin/shares/export.xlsx';

export const SURFACE_LABELS: Record<string, string> = {
  platform: 'Platform (in-app tech cards)',
  techpage: 'Public tech page',
  unipage: 'Public university page',
};
const SURFACE_SHORT: Record<string, string> = { platform: 'Platform', techpage: 'Tech page', unipage: 'University page' };
const SURFACE_TONE: Record<string, Tone> = { platform: 'info', techpage: 'good', unipage: 'neutral' };
const KIND_LABELS: Record<string, string> = { tech: 'Technology', university: 'University', ocean_problem: 'OCEAN problem', ocean_company: 'OCEAN company', ocean_collection: 'OCEAN collection' };
const EVENT_KIND: Record<string, { label: string; tone: Tone }> = {
  copy: { label: 'Copied', tone: 'neutral' },
  channel: { label: 'Shared to', tone: 'info' },
  native: { label: 'Share sheet', tone: 'info' },
  open: { label: 'Opened', tone: 'good' },
};

/** Table column → the server's sort key; other columns do not sort. */
const SORT_KEYS: Record<string, string> = {
  created_at: 'created', opens: 'opens', copies: 'copies', unique_ips: 'unique_ips', last_open_at: 'last_open',
  shares: 'shares', signed_in_opens: 'signed_in_opens',
};
/** by_channel / by_surface rows carry shares and opens (by_surface also links). */
type Metric = 'shares' | 'opens';
const METRIC_LABELS: Record<Metric, string> = { shares: 'Shares', opens: 'Opens' };

type Filters = { q: string; surface: string; channel: string; target_kind: string };
const EMPTY: Filters = { q: '', surface: '', channel: '', target_kind: '' };
/** Server defaults leave bot opens and the sharer's own opens out; these put them back. */
type Include = { bots: boolean; self: boolean };
const includeParams = (i: Include): Record<string, string> =>
  ({ ...(i.bots ? { include_bots: '1' } : {}), ...(i.self ? { include_self: '1' } : {}) });

const channelText = (m: any): string => {
  if (!m || typeof m !== 'object') return '';
  return Object.entries(m).filter(([, n]) => num(n) > 0)
    .sort((a, b) => num(b[1]) - num(a[1]))
    .map(([k, n]) => `${nameOf(k, SHARE_LABELS)} ${num(n)}`).join(' · ');
};

const SurfacePill: React.FC<{ s: any }> = ({ s }) => {
  const k = str(s);
  return k ? <Pill tone={SURFACE_TONE[k] || 'neutral'} title={SURFACE_LABELS[k] || k}>{SURFACE_SHORT[k] || k}</Pill>
    : <span className="text-grayscale-1100">—</span>;
};

/** The sharer: e-mail + lifecycle + tags, or the anonymous visitor's id. */
const Sharer: React.FC<{ row: any }> = ({ row }) => {
  const s = row?.sharer;
  if (s && (s.email || s.user_id)) {
    return (
      <span className="flex flex-col gap-0.5 leading-tight max-w-[18rem]">
        <span className="text-xs text-grayscale-1300 truncate" title={str(s.name) || str(s.email)}>{str(s.email) || str(s.user_id)}</span>
        <span className="inline-flex items-center gap-1 flex-wrap">
          <LifecyclePill value={s.lifecycle ?? null} label={s.lifecycle_label} />
          <TagPills tags={s.tags} max={2} nowrap />
        </span>
      </span>
    );
  }
  const v = str(row?.sharer_visitor_id);
  return <span className="text-xs text-grayscale-1100" title={v || undefined}>anonymous{v ? ` · ${v.slice(0, 8)}` : ''}</span>;
};

const Target: React.FC<{ row: any; onOpen?: () => void }> = ({ row, onOpen }) => (
  <span className="flex flex-col leading-tight max-w-[22rem]">
    {onOpen ? (
      <button type="button" onClick={onOpen} title="Open this link's detail"
        className="text-left font-medium text-grayscale-1300 underline underline-offset-2 truncate">
        {str(row.title) || str(row.target_id)}
      </button>
    ) : <span className="font-medium text-grayscale-1300 truncate">{str(row.title) || str(row.target_id)}</span>}
    <span className="text-[10px] font-mono text-grayscale-1100 truncate" title={str(row.path) || str(row.target_id)}>
      {KIND_LABELS[str(row.target_kind)] || str(row.target_kind)} · {str(row.path) || str(row.target_id)}
    </span>
  </span>
);

// ---------------------------------------------------------------------------
// Drawer — one link's story
// ---------------------------------------------------------------------------
const TIMELINE_COLS = ['at', 'kind', 'ip', 'where', 'user_agent', 'viewer', 'later_user'];
const TIMELINE_LABELS: Record<string, string> = {
  at: 'When', kind: 'What', ip: 'IP', where: 'Where', user_agent: 'Browser', viewer: 'Signed in as', later_user: 'Later signed in / up',
};

const LinkDrawer: React.FC<{ code: string; row?: any; include: Include; onClose: () => void }> = ({ code, row, include, onClose }) => {
  useEscape(onClose);
  const inc = includeParams(include);
  const res = useAdminGet(SECTION, 'link', { code, ...inc }, [code, JSON.stringify(inc)]);
  const d = res.data || {};
  const link = { ...(row || {}), ...(d.link || {}) };
  const events = rowsOf(d.events);
  const [copied, setCopied] = useState(false);
  const url = str(link.url);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { window.prompt('Copy this link:', url); }
  };
  const opens = events.filter((e: any) => str(e.kind) === 'open');
  const humanOpens = opens.filter((e: any) => (include.bots || !e.is_bot) && (include.self || !e.is_self));
  const count = (k: string, fallback: number) => (link[k] !== undefined && link[k] !== null ? num(link[k]) : fallback);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative h-full bg-grayscale-100 shadow-xl flex flex-col" style={{ width: 'min(96vw, 64rem)' }}
        onClick={e => e.stopPropagation()} role="dialog" aria-label={`Share link ${code}`}>
        <div className="px-5 py-4 border-b border-grayscale-300 bg-white flex items-start justify-between gap-3">
          <div className="min-w-0 flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <SurfacePill s={link.surface} />
              <span className="text-[11px] font-mono text-grayscale-1100">code {code}</span>
              {link.created_at && <span className="text-[11px] text-grayscale-1100">created {fmtDate(link.created_at)}</span>}
            </div>
            <div className="text-base font-bold text-grayscale-1300 font-grotesk break-words">{str(link.title) || str(link.target_id) || code}</div>
            <div className="text-xs text-grayscale-1100 flex items-center gap-2 flex-wrap">
              <span>{KIND_LABELS[str(link.target_kind)] || str(link.target_kind)} {str(link.target_id)}</span>
              {link.path && <OpenLink path={link.path} label="Open page ↗" />}
            </div>
            {url && (
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="font-mono break-all text-grayscale-1200">{url}</span>
                <Btn small onClick={copy}>{copied ? 'Copied' : 'Copy link'}</Btn>
              </div>
            )}
            <div className="text-xs">Shared by <Sharer row={link} /></div>
          </div>
          <Btn small onClick={onClose}>Close</Btn>
        </div>

        <div className="p-5 overflow-y-auto flex flex-col gap-4">
          {res.error && <Msg kind="error">{res.error}</Msg>}
          {res.loading && !res.data ? <Loading label="Reading this link's events…" /> : (
            <>
              <KPIRow>
                <KPI label="Copies" value={fmtInt(count('copies', events.filter((e: any) => str(e.kind) === 'copy').length))} hint="link copied" />
                <KPI label="Channel shares" value={fmtInt(link.channel_clicks && typeof link.channel_clicks === 'object'
                  ? Object.values(link.channel_clicks).reduce((a: number, n: any) => a + num(n), 0)
                  : events.filter((e: any) => ['channel', 'native'].includes(str(e.kind))).length)}
                  hint={channelText(link.channel_clicks) || 'LinkedIn, WhatsApp, X, e-mail, share sheet'} />
                <KPI label="Opens" value={fmtInt(count('opens', humanOpens.length))}
                  hint={[include.bots ? 'bots included' : 'people, not bots', include.self ? 'own opens included' : 'not the sharer\'s own',
                    num(link.bot_opens) ? `${num(link.bot_opens)} bot` : '', num(link.self_opens) ? `${num(link.self_opens)} own` : '']
                    .filter(Boolean).join(' · ')} />
                <KPI label="Unique IPs" value={fmtInt(count('unique_ips', new Set(humanOpens.map((e: any) => str(e.ip))).size))} />
                <KPI label="Signed-in opens" value={fmtInt(count('signed_in_opens', humanOpens.filter((e: any) => e.viewer).length))} />
                <KPI label="Openers signed up" value={fmtInt(count('openers_signed_up', humanOpens.filter((e: any) => e.later_user).length))}
                  hint="the visit later signed in / up" />
              </KPIRow>

              <Section title={`Timeline (${events.length})`}>
                <p className="text-xs text-grayscale-1100 -mt-1">
                  Every copy, share and open of this link, newest first. An open's IP, place and browser are the
                  visitor's; “Signed in as” is the account verified on the open, and “Later signed in / up” is an
                  account that visit or session signed into afterwards.
                </p>
                <DataTable rows={events} columns={TIMELINE_COLS} labels={TIMELINE_LABELS} maxHeight="36rem"
                  empty="Nothing recorded for this link yet."
                  render={{
                    at: r => <span className="text-xs whitespace-nowrap" title={str(r.at)}>{fmtDate(r.at)}</span>,
                    kind: r => {
                      const k = EVENT_KIND[str(r.kind)];
                      return (
                        <span className="inline-flex items-center gap-1 flex-wrap">
                          <Pill tone={k?.tone || 'neutral'}>{k?.label || str(r.kind) || '—'}</Pill>
                          {r.channel && str(r.kind) !== 'copy' && <span className="text-xs">{nameOf(r.channel, SHARE_LABELS)}</span>}
                          {r.is_bot && <Pill tone="warn" title="Flagged as a bot / crawler">bot</Pill>}
                          {r.is_self && <Pill tone="neutral" title="The sharer opening their own link">own open</Pill>}
                          {str(r.kind) === 'open' && r.open_channel && r.open_channel !== 'unknown' && (
                            <span className="text-[10px] text-grayscale-1100" title="The channel the opened link was shared through (its utm_medium)">
                              via {nameOf(r.open_channel, SHARE_LABELS)}
                            </span>
                          )}
                        </span>
                      );
                    },
                    ip: r => <span className="font-mono text-[11px]">{str(r.ip) || '—'}</span>,
                    where: r => (
                      <span className="flex flex-col leading-tight">
                        <span className="text-xs">{[str(r.ip_city), str(r.ip_country)].filter(Boolean).join(', ') || '—'}</span>
                        {r.device && <span className="text-[10px] text-grayscale-1100">{str(r.device)}</span>}
                      </span>
                    ),
                    user_agent: r => (
                      <span className="text-[11px] text-grayscale-1200 block max-w-[16rem] truncate" title={str(r.user_agent)}>
                        {str(r.user_agent) || '—'}
                      </span>
                    ),
                    viewer: r => (r.viewer && (r.viewer.email || r.viewer.user_id))
                      ? <span className="text-xs">{str(r.viewer.email) || str(r.viewer.user_id)}</span>
                      : <span className="text-xs text-grayscale-1100">{str(r.kind) === 'open' ? 'not signed in' : '—'}</span>,
                    later_user: r => (r.later_user && (r.later_user.email || r.later_user.user_id)) ? (
                      <span className="flex flex-col leading-tight">
                        <span className="text-xs">{str(r.later_user.email) || str(r.later_user.user_id)}</span>
                        {r.later_user.signed_up_at && <span className="text-[10px] text-grayscale-1100">signed up {fmtDate(r.later_user.signed_up_at)}</span>}
                      </span>
                    ) : <span className="text-xs text-grayscale-1100">—</span>,
                  }} />
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------
const LINK_COLS = ['created_at', 'title', 'surface', 'sharer', 'shares', 'copies', 'channel_clicks', 'opens', 'unique_ips',
  'signed_in_opens', 'openers_signed_up', 'last_open_at'];
const LINK_LABELS: Record<string, string> = {
  created_at: 'Created', title: 'Shared page', surface: 'Surface', sharer: 'Shared by', shares: 'Shares', copies: 'Copies',
  channel_clicks: 'Channels', opens: 'Opens', unique_ips: 'Unique IPs', signed_in_opens: 'Signed-in opens',
  openers_signed_up: 'Openers signed up', last_open_at: 'Last open',
};
const TOP_SHARER_COLS = ['email', 'lifecycle', 'links', 'shares', 'opens'];
const TOP_TARGET_COLS = ['title', 'kind', 'links', 'shares', 'opens'];

const Shares: React.FC<{ range: Range }> = ({ range }) => {
  const [f, setF] = useState<Filters>(EMPTY);
  const [tags, setTags] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>({ key: 'created_at', dir: 'desc' });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [tick, setTick] = useState(0);
  const [dlErr, setDlErr] = useState('');
  const [open, setOpen] = useState<{ code: string; row?: any } | null>(null);
  const [include, setInclude] = useState<Include>({ bots: false, self: false });
  const [metric, setMetric] = useState<Metric>('shares');
  const inc = includeParams(include);
  const q = useDebounced(f.q);
  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));
  const rp = rangeParams(range);

  // Overview: period + surface only (the contract's parameters).
  const ovParams = useMemo(() => ({ ...rp, ...(f.surface ? { surface: f.surface } : {}), ...inc }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(rp), f.surface, JSON.stringify(inc)]);
  const ov = useAdminGet(SECTION, 'overview', ovParams, [JSON.stringify(ovParams), tick]);
  const o = ov.data || {};
  const k = o.kpis || {};

  const filterParams = useMemo(() => {
    const p: Record<string, string | number> = {
      ...rp, q, surface: f.surface, channel: f.channel, target_kind: f.target_kind, ...tagFilterParams(tags), ...inc,
    };
    if (sort?.key && SORT_KEYS[sort.key]) { p.sort = SORT_KEYS[sort.key]; p.dir = sort.dir; }
    Object.keys(p).forEach(x => { if (p[x] === '' || p[x] === undefined) delete p[x]; });
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rp), q, f.surface, f.channel, f.target_kind, tags, sort, JSON.stringify(inc)]);
  const filterKey = JSON.stringify(filterParams);
  useEffect(() => { setPage(0); }, [filterKey, perPage]);
  // The server's page is 1-based; the pager's is 0-based.
  const res = useAdminGet(SECTION, 'links', { ...filterParams, page: page + 1, per_page: perPage }, [filterKey, page, perPage, tick]);
  const d = res.data || {};
  const rows = rowsOf(d, 'links');
  const per = Number(perPage) || 50;
  const { total, pages } = pageInfo(d, rows, per);

  // {channel|surface, shares, opens}; a plain {key: n} map or {n} rows from an older server still read.
  const metricOf = (x: any) => num(x?.[metric] ?? x?.n ?? x?.count ?? x?.value);
  const bars = (v: any, key: string, labels: Record<string, string>) => (Array.isArray(v)
    ? v.map((x: any) => ({ label: nameOf(x?.[key], labels), value: metricOf(x) }))
    : breakdown(v, k => nameOf(k, labels))).filter((x: any) => x.value > 0).sort((a: any, b: any) => b.value - a.value);
  const byChannel = bars(o.by_channel, 'channel', SHARE_LABELS);
  const bySurface = bars(o.by_surface, 'surface', SURFACE_LABELS);
  const byDay = rowsOf(o.by_day, 'days');
  const topSharers = rowsOf(o.top_sharers);
  const topTargets = rowsOf(o.top_targets);

  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) chips.push({ label: 'Search', value: f.q, clear: () => set('q')('') });
  if (f.surface) chips.push({ label: 'Surface', value: SURFACE_SHORT[f.surface] || f.surface, clear: () => set('surface')('') });
  if (f.channel) chips.push({ label: 'Channel', value: nameOf(f.channel, SHARE_LABELS), clear: () => set('channel')('') });
  if (f.target_kind) chips.push({ label: 'Target', value: KIND_LABELS[f.target_kind] || f.target_kind, clear: () => set('target_kind')('') });
  const anyFilter = chips.length > 0 || tagFilterActive(tags);

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border border-grayscale-300 bg-white px-4 py-2.5 flex items-center gap-2 flex-wrap text-xs">
        <span className="text-grayscale-1100">Surface</span>
        {['', 'platform', 'techpage', 'unipage'].map(s => (
          <button key={s || 'all'} type="button" onClick={() => set('surface')(s)} aria-pressed={f.surface === s}
            className={`py-1 px-2.5 rounded-full font-grotesk whitespace-nowrap ${f.surface === s
              ? 'bg-primary-500 text-grayscale-100 font-bold'
              : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
            {s ? SURFACE_LABELS[s] : 'All surfaces'}
          </button>
        ))}
        <span className="flex-1" />
        <label className="inline-flex items-center gap-1.5 whitespace-nowrap cursor-pointer" title="Count opens by crawlers and link-preview bots too">
          <input type="checkbox" checked={include.bots} onChange={e => setInclude(i => ({ ...i, bots: e.target.checked }))} />
          Include bot opens
        </label>
        <label className="inline-flex items-center gap-1.5 whitespace-nowrap cursor-pointer" title="Count the sharer opening their own link too">
          <input type="checkbox" checked={include.self} onChange={e => setInclude(i => ({ ...i, self: e.target.checked }))} />
          Include the sharer&apos;s own opens
        </label>
      </div>

      {ov.error && <Msg kind="error">{ov.error}</Msg>}
      {ov.loading && !ov.data ? <Loading label="Counting shares…" /> : (
        <>
          <KPIRow>
            <KPI label="Share links" value={fmtInt(k.links)} hint="one per sharer per page" />
            <KPI label="Sharers" value={fmtInt(k.sharers)} hint="accounts + anonymous visitors" />
            <KPI label="Copies" value={fmtInt(k.copies)} hint="link copied" />
            <KPI label="Channel shares" value={fmtInt(k.channel_clicks)} hint="LinkedIn, WhatsApp, X, e-mail, share sheet" />
            <KPI label="Opens" value={fmtInt(k.opens)} hint="shared links opened (people)" />
            <KPI label="Unique IPs" value={fmtInt(k.unique_ips)} hint="distinct openers" />
            <KPI label="Signed-in opens" value={fmtInt(k.signed_in_opens)} hint="opened while signed in" />
            <KPI label="Sign-ups after open" value={fmtInt(k.signups_after_open)} hint="opener later signed up" />
          </KPIRow>

          <Section title="Shares by day" right={<Btn small onClick={() => setTick(t => t + 1)} disabled={ov.loading || res.loading}>Refresh</Btn>}>
            <DailyBars days={byDay} empty="No shares in this period."
              series={[
                { key: 'shares', label: 'Shares' }, { key: 'opens', label: 'Opens' }, { key: 'links', label: 'New links' },
                { key: 'copies', label: 'Copies' }, { key: 'signed_in_opens', label: 'Signed-in opens' },
              ]} />
          </Section>

          <div className="flex items-center gap-2 text-xs text-grayscale-1200">
            <span className="text-grayscale-1100">Breakdowns by</span>
            {(['shares', 'opens'] as Metric[]).map(m => (
              <button key={m} type="button" onClick={() => setMetric(m)} aria-pressed={metric === m}
                className={`py-1 px-2.5 rounded-full font-grotesk whitespace-nowrap ${metric === m
                  ? 'bg-primary-500 text-grayscale-100 font-bold'
                  : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
                {METRIC_LABELS[m]}
              </button>
            ))}
            <span className="text-[11px] text-grayscale-1100">
              {metric === 'opens' ? 'opens by the channel the link was shared through (its utm_medium)' : 'copies, channel shares and share-sheet shares'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="By channel">
              {byChannel.length
                ? <BarList items={byChannel} activeLabel={f.channel ? nameOf(f.channel, SHARE_LABELS) : undefined}
                    onSelect={label => {
                      const key = Object.keys(SHARE_LABELS).find(c => SHARE_LABELS[c] === label) || label;
                      set('channel')(f.channel === key ? '' : key);
                    }} />
                : <div className="text-sm text-grayscale-1100">Nothing in this period.</div>}
              <div className="text-[11px] text-grayscale-1100">Click a channel to filter the links below.</div>
            </Section>
            <Section title="By surface">
              {bySurface.length
                ? <BarList items={bySurface} activeLabel={f.surface ? SURFACE_LABELS[f.surface] : undefined}
                    onSelect={label => {
                      const key = Object.keys(SURFACE_LABELS).find(s => SURFACE_LABELS[s] === label) || '';
                      set('surface')(f.surface === key ? '' : key);
                    }} />
                : <div className="text-sm text-grayscale-1100">Nothing in this period.</div>}
              <div className="text-[11px] text-grayscale-1100">
                Platform = the tech cards inside the app; the public pages are /tech/… and /universities/….
              </div>
            </Section>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section title="Top sharers">
              <DataTable rows={topSharers} columns={TOP_SHARER_COLS} maxHeight="20rem" empty="No sharers in this period."
                labels={{ email: 'Sharer', lifecycle: 'Lifecycle · tags', links: 'Links', shares: 'Shares', opens: 'Opens' }}
                render={{
                  email: r => (
                    <button type="button" className="text-left text-xs underline underline-offset-2 truncate max-w-[16rem]"
                      title="Show this sharer's links below" onClick={() => set('q')(str(r.email))}>
                      {str(r.email) || (r.user_id ? str(r.user_id) : `anonymous visitor${r.visitor_id ? ` · ${str(r.visitor_id).slice(0, 8)}` : ''}`)}
                    </button>
                  ),
                  lifecycle: r => (
                    <span className="inline-flex items-center gap-1">
                      <LifecyclePill value={r.lifecycle ?? null} label={r.lifecycle_label} />
                      <TagPills tags={r.tags} max={2} nowrap />
                    </span>
                  ),
                  links: r => <span className="tabular-nums">{fmtInt(r.links)}</span>,
                  shares: r => <span className="tabular-nums">{fmtInt(r.shares)}</span>,
                  opens: r => <span className="tabular-nums font-medium">{fmtInt(r.opens)}</span>,
                }} />
            </Section>
            <Section title="Most shared pages">
              <DataTable rows={topTargets} columns={TOP_TARGET_COLS} maxHeight="20rem" empty="Nothing shared in this period."
                labels={{ title: 'Page', kind: 'Kind', links: 'Links', shares: 'Shares', opens: 'Opens' }}
                render={{
                  title: r => (
                    <span className="inline-flex items-center gap-2 max-w-[20rem]">
                      <button type="button" className="text-left text-xs font-medium underline underline-offset-2 truncate"
                        title="Show this page's links below" onClick={() => set('q')(str(r.title) || str(r.id))}>
                        {str(r.title) || str(r.id)}
                      </button>
                      {r.path && <OpenLink path={r.path} label="↗" />}
                    </span>
                  ),
                  kind: r => KIND_LABELS[str(r.kind)] || str(r.kind) || '—',
                  links: r => <span className="tabular-nums">{fmtInt(r.links)}</span>,
                  shares: r => <span className="tabular-nums">{fmtInt(r.shares)}</span>,
                  opens: r => <span className="tabular-nums font-medium">{fmtInt(r.opens)}</span>,
                }} />
            </Section>
          </div>
        </>
      )}

      <Section title="Share links"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} link{total === 1 ? '' : 's'}</span>
            {anyFilter && <Btn small onClick={() => { setF(EMPTY); setTags(EMPTY_TAG_FILTER); }}>Clear filters</Btn>}
            <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
            <ExcelButton path={EXPORT_PATH} params={filterParams} onError={setDlErr} fallback="share-links" what="These share links" />
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1">
          One row per share link (a sharer × a page × a surface) in the period above. Opens count people, not
          crawlers. Click a page for the link's timeline: each copy, share and open with IP, place, browser and who it was.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide placeholder="Search sharer e-mail or page title…" />
          <Select value={f.channel} onChange={set('channel')} options={['', ...Object.keys(SHARE_LABELS)]}
            labels={{ '': 'Any channel', ...SHARE_LABELS }} />
          <Select value={f.target_kind} onChange={set('target_kind')} options={['', 'tech', 'university', 'ocean_problem', 'ocean_company', 'ocean_collection']}
            labels={{ '': 'Any target', tech: 'Technologies', university: 'Universities', ocean_problem: 'OCEAN problems', ocean_company: 'OCEAN companies', ocean_collection: 'OCEAN collections' }} />
        </div>
        <div className="rounded-lg border border-grayscale-200 px-3 py-2">
          <div className="text-[11px] text-grayscale-1100 mb-1">Sharer (signed-in accounts only)</div>
          <TagFilter value={tags} onChange={v => setTags(v)} hide={['invite_sent', 'signed_in']} />
        </div>
        {chips.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          </div>
        )}
        {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading label="Reading share links…" /> : (
          <DataTable rows={rows} columns={LINK_COLS} labels={LINK_LABELS} maxHeight="44rem"
            empty={anyFilter ? 'No share links match these filters.' : 'Nobody has shared anything in this period.'}
            serverSort={sort} onSortChange={(key, dir) => setSort(key && dir ? { key, dir } : null)}
            unsortable={LINK_COLS.filter(c => !SORT_KEYS[c])}
            render={{
              created_at: r => <span className="text-xs whitespace-nowrap">{fmtDate(r.created_at)}</span>,
              title: r => <Target row={r} onOpen={() => setOpen({ code: str(r.code), row: r })} />,
              surface: r => <SurfacePill s={r.surface} />,
              sharer: r => <Sharer row={r} />,
              shares: r => <span className="tabular-nums">{fmtInt(r.shares)}</span>,
              copies: r => <span className="tabular-nums">{fmtInt(r.copies)}</span>,
              channel_clicks: r => <span className="text-xs text-grayscale-1200">{channelText(r.channel_clicks) || '—'}</span>,
              opens: r => <span className="tabular-nums font-medium">{fmtInt(r.opens)}</span>,
              unique_ips: r => <span className="tabular-nums">{fmtInt(r.unique_ips)}</span>,
              signed_in_opens: r => <span className="tabular-nums">{fmtInt(r.signed_in_opens)}</span>,
              openers_signed_up: r => <span className="tabular-nums">{fmtInt(r.openers_signed_up)}</span>,
              last_open_at: r => <span className="text-xs whitespace-nowrap">{r.last_open_at ? fmtDate(r.last_open_at) : '—'}</span>,
            }}
            actions={r => (
              <span className="inline-flex items-center gap-2">
                <Btn small onClick={() => setOpen({ code: str(r.code), row: r })}>Timeline</Btn>
                <OpenLink path={r.path} />
              </span>
            )} />
        )}
        {pages > 1 && (
          <Pager page={page} pages={pages} total={total} shown={rows.length} per={per}
            onPage={setPage} perPage={perPage} onPerPage={setPerPage} />
        )}
      </Section>
      {open && open.code && <LinkDrawer code={open.code} row={open.row} include={include} onClose={() => setOpen(null)} />}
    </div>
  );
};

export default Shares;
